import { useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { Text, TextLink, useToast } from '@machobear/design-system'
import { useQuery } from '@tanstack/react-query'
import pick from 'lodash/pick'

import config from '@/config'
import {
  getCannedTemplateInquiries,
  postCannedTemplate,
  putCannedTemplate
} from '@/ports'
import { FetchResponse } from '@/ports/api'
import useCooldown from '@/hooks/use-cooldown'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import { useAlerts } from '@/components/alert/useAlerts.tsx'
import { queryClient } from '@/components/query-client'

import {
  contactSimilarListingFunc,
  sendInquiryMessage
} from './inquiryMessageUtil'

export type InquiryMessage = { id?: string; content: string }

export const useInquiryMessage = (isLoggedIn: boolean) => {
  const { t } = useTranslation()
  const { sendAlert } = useAlerts()
  const showToast = useToast()
  const { startCooldown, resetCooldown } = useCooldown()
  const fallback: InquiryMessage = {
    id: undefined,
    content: t(
      'b.listing.view.message_default.text',
      'Hi there, I saw your rental listing on liv.rent and would like to learn more. Please let me know what the next step would be. Thank you!'
    )
  }

  // Define custom function to handle localStorage for non-logged in users
  const getLocalStorageInquiry = (): FetchResponse<InquiryMessage[]> => {
    const storedMessage = localStorage.getItem('inquiry-message')
    return {
      response: { ok: true } as Response,
      body: storedMessage
        ? [{ id: undefined, content: storedMessage }]
        : [fallback]
    }
  }

  const queryFn = async (): Promise<FetchResponse<InquiryMessage[]>> => {
    if (isLoggedIn) {
      return getCannedTemplateInquiries() as Promise<
        FetchResponse<InquiryMessage[]>
      >
    } else {
      return getLocalStorageInquiry()
    }
  }

  const { data: messageData, isLoading } = useQuery(
    ['useInquiryMessage', isLoggedIn],
    queryFn,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      select: (data: FetchResponse<InquiryMessage[]>): InquiryMessage => {
        if (data.response.ok && data.body && data.body.length > 0) {
          return pick(data.body[0], ['id', 'content'])
        }
        return fallback
      }
    }
  )

  const showInquiryUndoToast = (params: {
    timeout: number
    onUndo: () => void
    onTimeout: () => void
  }) => {
    startCooldown(5, params.onTimeout)

    showToast({
      tone: 'positive' as const,
      message: t('Sending Message...'),
      description: (
        <Text>
          <TextLink
            href={''}
            onClick={e => {
              e.preventDefault()
              resetCooldown()
              params.onUndo()
            }}
          >
            {t('Undo')}
          </TextLink>
        </Text>
      )
    })
  }

  const showInquiryMessageToast = (params?: {
    listing: any
    contact_similar_listings: boolean
    formValues: any
  }) => {
    if (params) {
      const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        // TODO DSLoginModal
        showLoginModal({
          mode: 'register',
          eventId: '',
          data: {
            username: params.formValues.email,
            first_name: params.formValues.first_name,
            last_name: params.formValues.last_name,
            account_type: 'tenant'
          },
          redirectUrl: `/chat`,
          asyncAfterLogin: async () => {
            if (params.contact_similar_listings) {
              await sendAlert(params.listing)
            }
          },
          t
        })
      }
      showToast({
        tone: 'positive' as const,
        message: t('b.listing.view.message_sent.title', 'Message Sent!'),
        description: (
          <Text>
            <TextLink href={''} onClick={onClick}>
              {t('View chat messages')}
            </TextLink>
          </Text>
        )
      })
    } else {
      showToast({
        tone: 'positive' as const,
        message: t('b.listing.view.message_sent.title', 'Message Sent!'),
        description: (
          <Text>
            <TextLink href={`${config.DASHBOARD_URL}/chat`}>
              {t('View chat messages')}
            </TextLink>
          </Text>
        )
      })
    }
  }

  const sendInquiryMessageAfterLogin = useCallback(
    async (
      onSuccess: () => void,
      params?: {
        listingId: string
        formValues: any
        contact_similar_listings: boolean
      }
    ) => {
      if (!params) {
        return { ok: false, message: 'missing params' }
      }

      // TODO fix this shouldn't always be hit but it is
      if (!messageData?.id) {
        // create new inquiry message
        postCannedTemplate({
          body: {
            content: params.formValues.message,
            template_type: 'inquiries'
          }
        })
      } else {
        if (params.formValues.message != messageData.content) {
          putCannedTemplate(
            {
              body: { content: params.formValues.message }
            },
            { templateId: messageData.id }
          )
        }
      }
      queryClient.removeQueries(['useInquiryMessage'])

      const result1 = await sendInquiryMessage({
        isLoggedIn: true,
        listingId: params.listingId,
        formValues: pick(params.formValues, ['message', 'move_in_date'])
      })

      if (!result1.ok) {
        showToast({
          tone: 'critical',
          message: result1.message ?? 'Unable to contact the listing(s)'
        })
      }

      if (!params.contact_similar_listings) {
        onSuccess()
        return
      }

      const contactResult = await contactSimilarListingFunc({
        isLoggedIn: true,
        listingId: params.listingId,
        formValues: params.formValues
      })
      if (!contactResult.ok) {
        showToast({
          tone: 'critical',
          message: contactResult.message ?? 'Unable to contact the listing(s)'
        })
        return
      }

      onSuccess()
    },
    [messageData, showToast]
  )

  return {
    isLoading,
    message: messageData,
    defaultMessage: fallback,
    showInquiryMessageToast,
    showInquiryUndoToast,
    sendInquiryMessageAfterLogin
  }
}
